import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import chatService from '../../services/chatService';
import pusherService from '../../services/pusherService';
import MessageItem from './MessageItem';
import MessageInput from './MessageInput';
import './ChatPopup.css';

const ChatPopup = ({ userId, userName, userProfilePhoto, initialRoom = null, initialMessage = '', isMinimized: externalMinimized = false, onClose, onMinimize, onChatInitiated }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false); // Start with false - show popup immediately
  const [chatRoom, setChatRoom] = useState(initialRoom);
  const [isMinimized, setIsMinimized] = useState(externalMinimized);
  const [peerStatus, setPeerStatus] = useState({ is_online: false, last_seen_at: null });
  const [unreadCount, setUnreadCount] = useState(0);
  const pendingMessagesRef = useRef(new Map()); // Track pending optimistic messages
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const roomChannelRef = useRef(null);
  const roomChannelHandlersRef = useRef(null); // Store event handlers for proper cleanup
  const initializedRef = useRef(false);
  const lastReadMessageIdRef = useRef(null); // Track last read message ID
  const markAsReadTimeoutRef = useRef(null); // Debounce mark as read calls
  const observedMessagesRef = useRef(new Set()); // Track which messages we've observed
  const messagesCacheRef = useRef(new Map()); // Cache messages by room ID to persist across minimize/maximize
  const lastCacheUpdateRef = useRef(new Map()); // Track last cache update time per room
  const statusUpdateTimeoutRef = useRef(null); // Debounce status updates
  const lastStatusUpdateRef = useRef(null); // Track last status update to prevent duplicates

  // Helper function to get cache key
  const getCacheKey = useCallback((roomId) => {
    return roomId ? `room-${roomId}` : `user-${userId}`;
  }, [userId]);

  // Helper function to restore messages from cache
  const restoreFromCache = useCallback((roomId) => {
    const cacheKey = getCacheKey(roomId);
    const cachedMessages = messagesCacheRef.current.get(cacheKey);
    const lastUpdate = lastCacheUpdateRef.current.get(cacheKey);
    
    if (cachedMessages && cachedMessages.length > 0) {
      // Check if cache is still fresh (less than 5 minutes old)
      const cacheAge = lastUpdate ? Date.now() - lastUpdate : Infinity;
      const maxCacheAge = 5 * 60 * 1000; // 5 minutes
      
      if (cacheAge < maxCacheAge) {
        // Restore from cache immediately
        setMessages(cachedMessages);
        return true; // Cache hit
      }
    }
    return false; // Cache miss or stale
  }, [getCacheKey]);

  // Helper function to update cache
  const updateCache = useCallback((roomId, messages) => {
    if (!roomId || !messages || messages.length === 0) return;
    const cacheKey = getCacheKey(roomId);
    messagesCacheRef.current.set(cacheKey, [...messages]); // Create a copy to avoid reference issues
    lastCacheUpdateRef.current.set(cacheKey, Date.now());
  }, [getCacheKey]);

  useEffect(() => {
    // Reset initialization when userId changes
    initializedRef.current = false;
    setMessages([]);
    setChatRoom(null);
    setLoading(false);
    // Clear cache when switching users
    messagesCacheRef.current.clear();
    lastCacheUpdateRef.current.clear();
    
    // Show popup immediately, then load data in background
    initializeChat();
    
    return () => {
      unsubscribeFromRoomChannel();
      initializedRef.current = false;
    };
  }, [userId]);

  // Update chat room when initialRoom changes (from context)
  useEffect(() => {
    if (initialRoom && initialRoom.id && (!chatRoom || chatRoom.id !== initialRoom.id)) {
      setChatRoom(initialRoom);
      
      // Initialize unread count from room data
      if (initialRoom.unread_count !== undefined) {
        setUnreadCount(initialRoom.unread_count || 0);
      }
      
      // Update peer status from room data
      if (initialRoom.peers && initialRoom.peers.length > 0) {
        const peer = initialRoom.peers[0];
        setPeerStatus({
          is_online: peer.is_online || false,
          last_seen_at: peer.last_seen_at || null,
        });
      }
      
      // Load messages if room has changed
      if (initialRoom.messages && Array.isArray(initialRoom.messages) && initialRoom.messages.length > 0) {
        // Use is_read_receipt from backend for our own messages
        const loadedMessages = initialRoom.messages.slice(-50).map(msg => ({
          ...msg,
          is_read: msg.user_id === user?.id 
            ? (msg.is_read_receipt === true) 
            : msg.is_read
        }));
        setMessages(loadedMessages);
      } else if (initialRoom.id) {
        // Load messages in background
        setLoading(true);
        chatService.getMessages(initialRoom.id, { limit: 50 })
          .then(response => {
            if (response.success && response.data) {
              // Use is_read_receipt from backend for our own messages
              const loadedMessages = response.data.map(msg => ({
                ...msg,
                is_read: msg.user_id === user?.id 
                  ? (msg.is_read_receipt === true) 
                  : msg.is_read
              }));
              setMessages(loadedMessages);
            }
          })
          .catch(err => console.error('Error loading messages:', err))
          .finally(() => setLoading(false));
        
        subscribeToRoomChannel(initialRoom.id);
      }
    }
  }, [initialRoom?.id, restoreFromCache, updateCache, user?.id]);

  // Update minimized state from external prop
  useEffect(() => {
    setIsMinimized(externalMinimized);
  }, [externalMinimized]);

  // Sync cache whenever messages change (for real-time updates)
  useEffect(() => {
    if (chatRoom?.id && messages.length > 0 && !isMinimized) {
      updateCache(chatRoom.id, messages);
    }
  }, [messages, chatRoom?.id, isMinimized, updateCache]);

  // Update user status when popup opens/closes (debounced, only on state change)
  useEffect(() => {
    if (isMinimized) {
      // When minimized, mark as offline after delay (debounced)
      if (statusUpdateTimeoutRef.current) {
        clearTimeout(statusUpdateTimeoutRef.current);
      }
      statusUpdateTimeoutRef.current = setTimeout(() => {
        // Only update if we were previously online
        if (lastStatusUpdateRef.current !== false) {
          chatService.updateUserStatus(false);
          lastStatusUpdateRef.current = false;
        }
      }, 60000); // Wait 60 seconds before marking offline when minimized
      return () => {
        if (statusUpdateTimeoutRef.current) {
          clearTimeout(statusUpdateTimeoutRef.current);
        }
      };
    } else {
      // When opened, mark as online (only if not already online)
      if (statusUpdateTimeoutRef.current) {
        clearTimeout(statusUpdateTimeoutRef.current);
      }
      statusUpdateTimeoutRef.current = setTimeout(() => {
        // Only update if we were not already online
        if (lastStatusUpdateRef.current !== true) {
          chatService.updateUserStatus(true);
          lastStatusUpdateRef.current = true;
        }
      }, 500); // Small delay to debounce rapid open/close
      
      return () => {
        if (statusUpdateTimeoutRef.current) {
          clearTimeout(statusUpdateTimeoutRef.current);
        }
      };
    }
  }, [isMinimized]);

  useEffect(() => {
    // Listen for new messages via Pusher
    const handleNewMessage = (event) => {
      const data = event.detail;
      if (data.room_id === chatRoom?.id || (data.message && data.message.chat_room_id === chatRoom?.id)) {
        const newMessage = data.message || data;
        if (newMessage && newMessage.id) {
          // Skip if this is our own message - we already have it optimistically
          if (newMessage.user_id === user?.id) {
            // Update the optimistic message in place instead of replacing it
            const pendingKey = newMessage.message?.substring(0, 50) + newMessage.user_id;
            if (pendingMessagesRef.current.has(pendingKey)) {
              setMessages(prev => {
                // Find and update the optimistic message in place
                return prev.map(msg => {
                  if (msg.temp_id === pendingKey) {
                    // Update the optimistic message with real message data
                    return {
                      ...newMessage,
                      // Keep the same position, just update the data
                    };
                  }
                  return msg;
                });
              });
              pendingMessagesRef.current.delete(pendingKey);
            }
            return; // Don't add own message again
          }
          
          // If popup is minimized, increment unread count for new messages from others
          if (isMinimized && newMessage.user_id !== user?.id) {
            setUnreadCount(prev => prev + 1);
          }
          
          setMessages(prev => {
            // Check if message already exists
            if (prev.some(msg => msg.id === newMessage.id)) {
              return prev;
            }
            
            // If this is a pending optimistic message, update it in place
            const pendingKey = newMessage.message?.substring(0, 50) + newMessage.user_id;
            if (pendingMessagesRef.current.has(pendingKey)) {
              pendingMessagesRef.current.delete(pendingKey);
              // Update the optimistic message in place instead of replacing it
              return prev.map(msg => {
                if (msg.temp_id === pendingKey) {
                  // Update the optimistic message with real message data
                  return {
                    ...newMessage,
                    is_read: newMessage.user_id === user?.id 
                      ? (newMessage.is_read_receipt === true)
                      : newMessage.is_read,
                    // Keep the same position, just update the data
                  };
                }
                return msg;
              });
            }
            
            // Use is_read_receipt from backend for our own messages
            const isRead = newMessage.user_id === user?.id 
              ? (newMessage.is_read_receipt === true)
              : newMessage.is_read;
            
            // Add new message without reloading - just update state
            return [...prev, { ...newMessage, is_read: isRead }];
          });
          
          // Only scroll if popup is not minimized
          if (!isMinimized) {
            scrollToBottom();
          }
        }
      }
    };

    // Listen for user online/offline events
    const handleUserOnline = (event) => {
      const data = event.detail;
      if (data && data.user_id === userId) {
        setPeerStatus({
          is_online: true,
          last_seen_at: data.last_seen_at || null,
        });
      }
    };

    const handleUserOffline = (event) => {
      const data = event.detail;
      if (data && data.user_id === userId) {
        setPeerStatus({
          is_online: false,
          last_seen_at: data.last_seen_at || null,
        });
      }
    };

    // Listen for messages read event (when peer reads messages we sent)
    const handleMessagesRead = (event) => {
      const data = event.detail;
      if (data && data.room_id && String(data.room_id) === String(chatRoom?.id) && data.user_id !== user?.id) {
        // Update read status only for messages that are currently unread
        // This is for read receipts - showing that the peer has read our messages
        setMessages(prev => prev.map(msg => {
          // Only update our own messages that are not yet marked as read
          if (msg.user_id === user?.id && !msg.is_read) {
            return { ...msg, is_read: true };
          }
          return msg;
        }));
      }
    };

    // Listen for unread count updates
    const handleUnreadCountUpdate = (event) => {
      const data = event.detail;
      if (data && data.unread_counts && chatRoom?.id) {
        const unreadCount = data.unread_counts[chatRoom.id] || 0;
        setUnreadCount(unreadCount);
      }
    };

    window.addEventListener('pusher:new-message', handleNewMessage);
    window.addEventListener('pusher:user-online', handleUserOnline);
    window.addEventListener('pusher:user-offline', handleUserOffline);
    window.addEventListener('pusher:messages-read', handleMessagesRead);
    window.addEventListener('pusher:chat-unread-count-updated', handleUnreadCountUpdate);

    return () => {
      window.removeEventListener('pusher:new-message', handleNewMessage);
      window.removeEventListener('pusher:user-online', handleUserOnline);
      window.removeEventListener('pusher:user-offline', handleUserOffline);
      window.removeEventListener('pusher:messages-read', handleMessagesRead);
      window.removeEventListener('pusher:chat-unread-count-updated', handleUnreadCountUpdate);
    };
  }, [chatRoom?.id, user?.id, userId, isMinimized]); // Added isMinimized to track unread count

  useEffect(() => {
    scrollToBottom();
  }, [messages.length]);

  // Mark messages as read when they become visible (scroll-based)
  useEffect(() => {
    if (isMinimized || !chatRoom?.id || !messagesContainerRef.current) {
      return;
    }

    const container = messagesContainerRef.current;
    const messageElements = container.querySelectorAll('[data-message-id]');
    
    if (messageElements.length === 0) {
      return;
    }

    // Create Intersection Observer to detect when messages are visible
    const observer = new IntersectionObserver(
      (entries) => {
        let hasUnreadVisible = false;
        
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const messageElement = entry.target;
            const messageId = messageElement.getAttribute('data-message-id');
            
            if (messageId && !observedMessagesRef.current.has(messageId)) {
              // Find the message in our state
              const message = messages.find(msg => String(msg.id) === messageId);
              
              // Check if this is an unread message from others
              if (message && message.user_id !== user?.id && !message.is_read) {
                hasUnreadVisible = true;
                observedMessagesRef.current.add(messageId);
              }
            }
          }
        });

        // Debounce mark as read call
        if (hasUnreadVisible) {
          if (markAsReadTimeoutRef.current) {
            clearTimeout(markAsReadTimeoutRef.current);
          }
          
          markAsReadTimeoutRef.current = setTimeout(() => {
            // Mark all unread messages as read
            chatService.markMessagesAsRead(chatRoom.id)
              .then(() => {
                // Update local unread count
                setUnreadCount(0);
                // Update message read status locally
                setMessages(prev => prev.map(msg => ({
                  ...msg,
                  is_read: msg.user_id === user?.id ? msg.is_read : true
                })));
                // Track last read message
                if (messages.length > 0) {
                  const lastMessage = messages[messages.length - 1];
                  if (lastMessage) {
                    lastReadMessageIdRef.current = lastMessage.id;
                  }
                }
              })
              .catch(err => console.error('Error marking messages as read:', err));
          }, 500); // 500ms debounce
        }
      },
      {
        root: container,
        rootMargin: '0px',
        threshold: 0.1 // Trigger when 10% of message is visible
      }
    );

    // Observe all message elements
    messageElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
      if (markAsReadTimeoutRef.current) {
        clearTimeout(markAsReadTimeoutRef.current);
      }
    };
  }, [messages, isMinimized, chatRoom?.id, user?.id]);

  const initializeChat = async () => {
    // Mark as initialized immediately
    initializedRef.current = true;
    
    // Don't set loading to true initially - show popup immediately
    // Load data in background
    
    try {
        // If initial room is provided, use it
      if (initialRoom && initialRoom.id) {
        setChatRoom(initialRoom);
        
        // Initialize unread count from room data
        if (initialRoom.unread_count !== undefined) {
          setUnreadCount(initialRoom.unread_count || 0);
        }
        
        // Update peer status from initial room data
        if (initialRoom.peers && initialRoom.peers.length > 0) {
          const peer = initialRoom.peers[0];
          setPeerStatus({
            is_online: peer.is_online || false,
            last_seen_at: peer.last_seen_at || null,
          });
        }
        
        // Try to restore from cache first
        const cacheHit = restoreFromCache(initialRoom.id);
        
        // Load messages for the room in background
        if (initialRoom.messages && Array.isArray(initialRoom.messages) && initialRoom.messages.length > 0) {
          // Messages already in room data - show immediately
          const loadedMessages = initialRoom.messages.slice(-50).map(msg => ({
            ...msg,
            is_read: msg.user_id === user?.id 
              ? (msg.is_read_receipt === true)
              : msg.is_read
          }));
          setMessages(loadedMessages);
          updateCache(initialRoom.id, loadedMessages);
          // Track last read message
          if (loadedMessages.length > 0) {
            const lastMessage = loadedMessages[loadedMessages.length - 1];
            if (lastMessage.is_read || lastMessage.user_id === user?.id) {
              lastReadMessageIdRef.current = lastMessage.id;
            }
          }
        } else if (!cacheHit) {
          // Load messages for existing room in background (only if cache miss)
          setLoading(true);
          try {
            const messagesResponse = await chatService.getMessages(initialRoom.id, { limit: 50 });
            if (messagesResponse.success && messagesResponse.data) {
              // Use is_read_receipt from backend for our own messages
              const loadedMessages = messagesResponse.data.map(msg => ({
                ...msg,
                is_read: msg.user_id === user?.id 
                  ? (msg.is_read_receipt === true)
                  : msg.is_read
              }));
              setMessages(loadedMessages);
              updateCache(initialRoom.id, loadedMessages);
              // Track last read message
              if (loadedMessages.length > 0) {
                const lastMessage = loadedMessages[loadedMessages.length - 1];
                if (lastMessage.is_read || lastMessage.user_id === user?.id) {
                  lastReadMessageIdRef.current = lastMessage.id;
                }
              }
            }
          } catch (err) {
            console.error('Error loading messages:', err);
            setMessages([]);
          } finally {
            setLoading(false);
          }
        }
        
        // Subscribe to room channel for real-time updates
        subscribeToRoomChannel(initialRoom.id);
        return;
      }
      
      // Check if chat exists with this user (in background)
      setLoading(true);
      const response = await chatService.getPeerToPeerChatInfo(userId);
      
      if (response.success && response.data) {
        const room = response.data.room;
        const peer = response.data.peer;
        setChatRoom(room);
        
        // Update peer status from response
        if (peer) {
          setPeerStatus({
            is_online: peer.is_online || false,
            last_seen_at: peer.last_seen_at || null,
          });
        } else if (room && room.peers && room.peers.length > 0) {
          const roomPeer = room.peers[0];
          setPeerStatus({
            is_online: roomPeer.is_online || false,
            last_seen_at: roomPeer.last_seen_at || null,
          });
        }
        
        // Initialize unread count from room data
        if (room && room.unread_count !== undefined) {
          setUnreadCount(room.unread_count || 0);
        }
        
        // Try to restore from cache first
        const cacheHit = restoreFromCache(room?.id);
        
        // Load messages if chat exists
        if (room && room.messages && Array.isArray(room.messages) && room.messages.length > 0) {
          // Messages already in room data
          const loadedMessages = room.messages.slice(-50).map(msg => ({
            ...msg,
            is_read: msg.user_id === user?.id 
              ? (msg.is_read_receipt === true)
              : msg.is_read
          }));
          setMessages(loadedMessages);
          updateCache(room.id, loadedMessages);
          // Track last read message
          if (loadedMessages.length > 0) {
            const lastMessage = loadedMessages[loadedMessages.length - 1];
            if (lastMessage.is_read || lastMessage.user_id === user?.id) {
              lastReadMessageIdRef.current = lastMessage.id;
            }
          }
          setLoading(false);
        } else if (room && room.id && !cacheHit) {
          // Load messages for existing room (only if cache miss)
          try {
            const messagesResponse = await chatService.getMessages(room.id, { limit: 50 });
            if (messagesResponse.success && messagesResponse.data) {
              // Use is_read_receipt from backend for our own messages
              const loadedMessages = messagesResponse.data.map(msg => ({
                ...msg,
                is_read: msg.user_id === user?.id 
                  ? (msg.is_read_receipt === true)
                  : msg.is_read
              }));
              setMessages(loadedMessages);
              updateCache(room.id, loadedMessages);
              // Track last read message
              if (loadedMessages.length > 0) {
                const lastMessage = loadedMessages[loadedMessages.length - 1];
                if (lastMessage.is_read || lastMessage.user_id === user?.id) {
                  lastReadMessageIdRef.current = lastMessage.id;
                }
              }
            }
          } catch (err) {
            console.error('Error loading messages:', err);
            setMessages([]);
          } finally {
            setLoading(false);
          }
        } else {
          setMessages([]);
          setLoading(false);
        }
        
        // Subscribe to room channel for real-time updates if room exists
        if (room && room.id) {
          subscribeToRoomChannel(room.id);
        }
      } else {
        // No chat exists yet - will be created when first message is sent
        setChatRoom(null);
        setMessages([]);
        setLoading(false);
        setPeerStatus({ is_online: false, last_seen_at: null });
      }
    } catch (err) {
      console.error('Error initializing chat:', err);
      setChatRoom(null);
      setMessages([]);
      setLoading(false);
    }
  };

  const subscribeToRoomChannel = (roomId) => {
    if (!roomId || !pusherService.getPusher()) return;

    const channelName = `chat-room-${roomId}`;
    const pusher = pusherService.getPusher();
    
    // Check if already subscribed to this channel
    if (roomChannelRef.current && roomChannelRef.current.name === channelName) {
      return; // Already subscribed, skip
    }
    
    // Unsubscribe from previous channel if exists
    unsubscribeFromRoomChannel();
    
    try {
      const channel = pusher.subscribe(channelName);
      roomChannelRef.current = channel;

      // Store handler reference for proper cleanup
      const newMessageHandler = (data) => {
        if (data.message) {
          // Skip if this is our own message - we already have it optimistically
          if (data.message.user_id === user?.id) {
            // Update the optimistic message in place instead of replacing it
            const pendingKey = data.message.message?.substring(0, 50) + data.message.user_id;
            if (pendingMessagesRef.current.has(pendingKey)) {
              setMessages(prev => {
                // Find and update the optimistic message in place
                return prev.map(msg => {
                  if (msg.temp_id === pendingKey) {
                    // Update the optimistic message with real message data
                    return {
                      ...data.message,
                      is_read: data.message.user_id === user?.id 
                        ? (data.message.is_read_receipt === true)
                        : data.message.is_read,
                      // Keep the same position, just update the data
                    };
                  }
                  return msg;
                });
              });
              pendingMessagesRef.current.delete(pendingKey);
            }
            return; // Don't add own message again
          }
          
          setMessages(prev => {
            // Check if message already exists
            if (prev.some(msg => msg.id === data.message.id)) {
              // Update existing message with latest read receipt status
              return prev.map(msg => {
                if (msg.id === data.message.id) {
                  return {
                    ...data.message,
                    is_read: data.message.user_id === user?.id 
                      ? (data.message.is_read_receipt === true)
                      : data.message.is_read
                  };
                }
                return msg;
              });
            }
            
            // If this is a pending optimistic message, update it in place
            const pendingKey = data.message.message?.substring(0, 50) + data.message.user_id;
            if (pendingMessagesRef.current.has(pendingKey)) {
              pendingMessagesRef.current.delete(pendingKey);
              // Update the optimistic message in place instead of replacing it
              return prev.map(msg => {
                if (msg.temp_id === pendingKey) {
                  return {
                    ...data.message,
                    is_read: data.message.user_id === user?.id 
                      ? (data.message.is_read_receipt === true)
                      : data.message.is_read,
                    // Keep the same position, just update the data
                  };
                }
                return msg;
              });
            }
            
            // Use is_read_receipt from backend for our own messages
            const isRead = data.message.user_id === user?.id 
              ? (data.message.is_read_receipt === true)
              : data.message.is_read;
            
            // Add new message - NO RELOAD
            return [...prev, { ...data.message, is_read: isRead }];
          });
          scrollToBottom();
        }
      };
      
      // Bind event and store handler reference
      channel.bind('new-message', newMessageHandler);
      roomChannelHandlersRef.current = {
        'new-message': newMessageHandler
      };
    } catch (err) {
      console.error('Error subscribing to room channel:', err);
    }
  };

  const unsubscribeFromRoomChannel = () => {
    if (roomChannelRef.current) {
      const pusher = pusherService.getPusher();
      if (pusher && roomChannelHandlersRef.current) {
        // Unbind all event handlers before unsubscribing
        Object.entries(roomChannelHandlersRef.current).forEach(([event, handler]) => {
          try {
            roomChannelRef.current.unbind(event, handler);
          } catch (err) {
            console.error(`Error unbinding ${event} handler:`, err);
          }
        });
        roomChannelHandlersRef.current = null;
      }
      
      if (pusher) {
        try {
          pusher.unsubscribe(roomChannelRef.current.name);
        } catch (err) {
          console.error('Error unsubscribing from channel:', err);
        }
      }
      roomChannelRef.current = null;
    }
  };

  const handleSendMessage = async (message, type = 'text') => {
    if (!message.trim()) return;

    // Don't update status on every message - status is already managed by popup open/close

    // Create optimistic message (temporary message shown immediately)
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    const pendingKey = message.substring(0, 50) + user?.id;
    const optimisticMessage = {
      id: tempId,
      temp_id: pendingKey,
      chat_room_id: chatRoom?.id,
      user_id: user?.id,
      user_name: user?.name,
      user_profile_photo: user?.profile_photo,
      message: message,
      type: type,
      created_at: new Date().toISOString(),
      is_read: false,
      is_pending: true, // Flag to identify pending messages
    };

    // Add optimistic message immediately
    setMessages(prev => [...prev, optimisticMessage]);
    pendingMessagesRef.current.set(pendingKey, optimisticMessage);
    scrollToBottom();

    // Send message in background - don't block UI
    let roomId = chatRoom?.id;
    
    // If no chat room exists, use peer-to-peer message endpoint which will create the room
    if (!roomId) {
      chatService.sendPeerToPeerMessage(userId, message, type)
        .then(response => {
          if (response.success) {
            const sentMessage = response.data;
            
            // Get the room ID from the message
            if (sentMessage.chat_room_id) {
              roomId = sentMessage.chat_room_id;
              
              // Reload chat info to get the full room data
              chatService.getPeerToPeerChatInfo(userId)
                .then(roomResponse => {
                  if (roomResponse.success && roomResponse.data) {
                    const newRoom = roomResponse.data.room;
                    const peer = roomResponse.data.peer;
                    setChatRoom(newRoom);
                    
                    // Update peer status from response
                    if (peer) {
                      setPeerStatus({
                        is_online: peer.is_online || false,
                        last_seen_at: peer.last_seen_at || null,
                      });
                    } else if (newRoom && newRoom.peers && newRoom.peers.length > 0) {
                      const roomPeer = newRoom.peers[0];
                      setPeerStatus({
                        is_online: roomPeer.is_online || false,
                        last_seen_at: roomPeer.last_seen_at || null,
                      });
                    }
                    
                    subscribeToRoomChannel(roomId);
                    
                    // Notify parent that chat was initiated
                    if (onChatInitiated) {
                      onChatInitiated(newRoom);
                    }
                  }
                })
                .catch(err => console.error('Error loading room info:', err));
            }
            
            // Message will be updated via Pusher event - NO RELOAD
            // If Pusher doesn't fire within 2 seconds, update manually
            setTimeout(() => {
              if (pendingMessagesRef.current.has(pendingKey) && sentMessage && sentMessage.id) {
                setMessages(prev => {
                  // Update the optimistic message in place instead of replacing it
                  return prev.map(msg => {
                    if (msg.temp_id === pendingKey) {
                      // Update the optimistic message with real message data
                      return {
                        ...sentMessage,
                        is_read: sentMessage.user_id === user?.id 
                          ? (sentMessage.is_read_receipt === true)
                          : sentMessage.is_read,
                        // Keep the same position, just update the data and remove pending flag
                      };
                    }
                    return msg;
                  });
                });
                pendingMessagesRef.current.delete(pendingKey);
              }
            }, 2000);
          } else {
            // Remove optimistic message on failure - NO RELOAD
            setMessages(prev => prev.filter(msg => msg.temp_id !== pendingKey));
            pendingMessagesRef.current.delete(pendingKey);
            console.error('Failed to send message');
          }
        })
        .catch(err => {
          console.error('Error sending message:', err);
          // Remove optimistic message on error
          setMessages(prev => prev.filter(msg => msg.temp_id !== pendingKey));
          pendingMessagesRef.current.delete(pendingKey);
        });
    } else {
      // Chat room exists, send message normally
      chatService.sendMessage(roomId, message, type)
        .then(response => {
          if (response.success) {
            const sentMessage = response.data;
            
            // Message will be updated via Pusher event - NO RELOAD
            // If Pusher doesn't fire within 2 seconds, update manually
            setTimeout(() => {
              if (pendingMessagesRef.current.has(pendingKey) && sentMessage && sentMessage.id) {
                setMessages(prev => {
                  // Update the optimistic message in place instead of replacing it
                  return prev.map(msg => {
                    if (msg.temp_id === pendingKey) {
                      // Update the optimistic message with real message data
                      return {
                        ...sentMessage,
                        is_read: sentMessage.user_id === user?.id 
                          ? (sentMessage.is_read_receipt === true)
                          : sentMessage.is_read,
                        // Keep the same position, just update the data and remove pending flag
                      };
                    }
                    return msg;
                  });
                });
                pendingMessagesRef.current.delete(pendingKey);
              }
            }, 2000);
          } else {
            // Remove optimistic message on failure - NO RELOAD
            setMessages(prev => prev.filter(msg => msg.temp_id !== pendingKey));
            pendingMessagesRef.current.delete(pendingKey);
          }
        })
        .catch(err => {
          console.error('Error sending message:', err);
          // Remove optimistic message on error
          setMessages(prev => prev.filter(msg => msg.temp_id !== pendingKey));
          pendingMessagesRef.current.delete(pendingKey);
        });
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      // Check if we're at bottom and mark messages as read
      checkAndMarkAsRead();
    }, 100);
  };

  // Check if user is at bottom and mark all messages as read
  const checkAndMarkAsRead = useCallback(() => {
    if (isMinimized || !chatRoom?.id || !messagesContainerRef.current) {
      return;
    }

    const container = messagesContainerRef.current;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // Check if user is near bottom (within 100px)
    const isNearBottom = (scrollHeight - scrollTop - clientHeight) < 100;
    
    if (isNearBottom && unreadCount > 0) {
      // Debounce to avoid multiple calls
      if (markAsReadTimeoutRef.current) {
        clearTimeout(markAsReadTimeoutRef.current);
      }
      
      markAsReadTimeoutRef.current = setTimeout(() => {
        // Mark all messages as read when at bottom
        chatService.markMessagesAsRead(chatRoom.id)
          .then(() => {
            setUnreadCount(0);
            setMessages(prev => prev.map(msg => ({
              ...msg,
              is_read: msg.user_id === user?.id ? msg.is_read : true
            })));
          })
          .catch(err => console.error('Error marking messages as read:', err));
      }, 300); // 300ms debounce for scroll
    }
  }, [isMinimized, chatRoom?.id, unreadCount, user?.id]);

  // Format last seen time
  const formatLastSeen = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  };

  const handleMinimize = () => {
    const newMinimized = !isMinimized;
    setIsMinimized(newMinimized);
    
    // Save messages to cache when minimizing
    if (newMinimized && chatRoom?.id && messages.length > 0) {
      updateCache(chatRoom.id, messages);
    }
    
    // Restore from cache when opening from minimized state
    if (!newMinimized && chatRoom?.id) {
      const cacheHit = restoreFromCache(chatRoom.id);
      if (cacheHit) {
        // Cache restored, no need to reload
        observedMessagesRef.current.clear();
      }
    } else if (!newMinimized) {
      observedMessagesRef.current.clear();
    }
    
    if (onMinimize) {
      onMinimize();
    }
  };

  if (isMinimized) {
    return (
      <div className={`chat-popup-minimized ${unreadCount > 0 ? 'has-unread' : ''}`} onClick={handleMinimize}>
        <div className="chat-popup-minimized-avatar">
          {userProfilePhoto ? (
            <img src={userProfilePhoto} alt={userName} />
          ) : (
            <div className="chat-popup-avatar-placeholder">
              {userName?.charAt(0).toUpperCase() || 'U'}
            </div>
          )}
          {peerStatus.is_online && (
            <span className="chat-popup-minimized-online-indicator"></span>
          )}
        </div>
        <div className="chat-popup-minimized-info">
          <div className="chat-popup-minimized-name-row">
            <span className="chat-popup-minimized-name">{userName}</span>
            {unreadCount > 0 && (
              <span className="chat-popup-unread-badge">{unreadCount}</span>
            )}
          </div>
          <span className="chat-popup-minimized-status">
            {peerStatus.is_online ? (
              <span className="chat-popup-minimized-online">
                <span className="chat-popup-minimized-online-dot"></span>
                Online
              </span>
            ) : peerStatus.last_seen_at ? (
              `Last seen ${formatLastSeen(peerStatus.last_seen_at)}`
            ) : (
              'Offline'
            )}
          </span>
        </div>
        <button className="chat-popup-close-btn" onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="chat-popup">
      <div className="chat-popup-header">
        <div className="chat-popup-header-info">
          {userProfilePhoto ? (
            <img src={userProfilePhoto} alt={userName} />
          ) : (
            <div className="chat-popup-avatar-placeholder">
              {userName?.charAt(0).toUpperCase() || 'U'}
            </div>
          )}
          <div>
            <h3>{userName}</h3>
            <p className="chat-popup-status">
              {peerStatus.is_online ? (
                <span className="online-status">
                  <span className="online-indicator"></span>
                  Online
                </span>
              ) : peerStatus.last_seen_at ? (
                `Last seen ${formatLastSeen(peerStatus.last_seen_at)}`
              ) : (
                'Offline'
              )}
            </p>
          </div>
        </div>
        <div className="chat-popup-header-actions">
          <button className="chat-popup-minimize-btn" onClick={handleMinimize} title="Minimize">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
          <button className="chat-popup-close-btn" onClick={onClose} title="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>

      <div 
        className="chat-popup-messages" 
        ref={messagesContainerRef}
        onScroll={checkAndMarkAsRead}
      >
        {loading && messages.length === 0 ? (
          <div className="chat-popup-loading">
            <div className="chat-spinner"></div>
            <span>Loading messages...</span>
          </div>
        ) : messages.length === 0 ? (
          <div className="chat-popup-empty">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          <>
            {loading && (
              <div className="chat-popup-loading-top">
                <div className="chat-spinner-small"></div>
              </div>
            )}
            {messages.map((message) => (
              <div key={message.id} data-message-id={message.id}>
                <MessageItem
                  message={message}
                  isOwn={message.user_id === user?.id}
                />
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      <div className="chat-popup-input-container">
        <MessageInput
          onSendMessage={handleSendMessage}
          initialMessage={initialMessage}
        />
      </div>
    </div>
  );
};

export default ChatPopup;

